import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import React, { useCallback, useMemo } from 'react'
import { Cell } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { User } from '@/api/types'
import { Button } from '@/components/Button'
import { Grid } from '@/components/Grid'
import { Label } from '@/components/Label'
import { PageEditHeader } from '@/components/PageEditHeader'
import { Select } from '@/components/Select'
import { Column, Row, Table, TableBody, TableHeader, TableSearch } from '@/components/Table'
import CreateUserDialog from './CreateUserDialog'
import ResetPasswordDialog from './ResetPasswordDialog'
import { UsersSearchParams } from '@/routes/users.index'
import { Flex } from '@/components/Flex'

type Props = {
  users: User[]
  dbContexts: Record<string, string>
  currContext: string
  deleteUser: (idUser: number) => void
  createUser: (newUser: any, changePassword: boolean) => void
  resetPassword: (idUser: number, password: string) => void
}

const routeApi = getRouteApi('/users')

function filterUsers(users: User[], query?: string) {
  if (!query) return users
  return users.filter(
    (user) =>
      user.email.toLowerCase().includes(query.toLowerCase()) ||
      user.idUser.toString().includes(query.toLowerCase()),
  )
}

export default function UsersIndex({
  users,
  dbContexts,
  currContext,
  deleteUser,
  createUser,
  resetPassword,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const searchParams: UsersSearchParams = routeApi.useSearch()
  const filteredUsers = useMemo(() => filterUsers(users, searchParams.q), [users, searchParams.q])

  const handleSearch = useCallback((query: string) => {
    navigate({
      from: '/users',
      search: (search) => ({
        ...search,
        q: query,
      }),
    })
  }, [])

  const handleDeleteUser = useCallback((idUser: number) => {
    if (confirm(t('users.confirmDelete'))) {
      deleteUser(idUser)
    }
  }, [])

  const handleChangeContext = useCallback((selection: any) => {
    navigate({
      from: '/users',
      search: (search) => ({
        ...search,
        dbContext: selection?.value,
      }),
    })
  }, [])

  const contextOptions = Object.entries(dbContexts).map(([key, value]) => ({
    value: key,
    label: value,
  }))

  return (
    <div>
      <PageEditHeader title={t('users.title')}>
        <CreateUserDialog
          dbContexts={dbContexts}
          defaultContext={currContext}
          createUser={createUser}
        />
      </PageEditHeader>

      <Grid columns="320px 1fr" style={{ alignItems: 'flex-start' }}>
        <div className="panel-xs">
          <div>
            <Label>{t('users.dbContext')}</Label>
            <Select
              value={{ value: currContext, label: dbContexts[currContext] }}
              options={contextOptions}
              onChange={handleChangeContext}
              required
            />
          </div>
        </div>
        <Flex direction="column" gap={4}>
          <TableSearch
            handleSearch={handleSearch}
            defaultValue={searchParams.q}
            style={{ width: '100%' }}
          />
          <Table aria-label="Admin table">
            <TableHeader>
              <Column id="externalID" isRowHeader>
                ID
              </Column>
              <Column id="email" allowsSorting>
                {t('users.email')}
              </Column>
              <Column id="action">-</Column>
            </TableHeader>
            <TableBody items={filteredUsers}>
              {(user) => (
                <Row key={user.idUser} id={user.idUser}>
                  <Cell>{user.idUser}</Cell>
                  <Cell>
                    <Link
                      from="/users"
                      to="/users/$dbcontext/$id"
                      params={{
                        dbcontext: currContext,
                        id: user.idUser.toString(),
                      }}>
                      {user.email}
                    </Link>
                  </Cell>
                  <Cell>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <ResetPasswordDialog
                        resetUserPassword={(password) => resetPassword(user.idUser, password)}
                      />
                      <Button variant="negative" onPress={() => handleDeleteUser(user.idUser)}>
                        {t('users.deleteUser')}
                      </Button>
                    </div>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </Flex>
      </Grid>
    </div>
  )
}
